import React from "react";
import { TextField } from "braid-design-system";
import { sanitizeNumericString, NumericFilterOptions } from "../utils/numericInput";

export interface NumericTextFieldProps extends NumericFilterOptions {
    id?: string;
    label: string;
    value: string | number | null | undefined;
    onChange: (nextValue: string) => void;
    /** Called when a keystroke/paste was filtered out (e.g. to surface a "numbers only" error). */
    onInvalid?: () => void;
    tone?: "critical";
    message?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Drop-in replacement for Braid's TextField for numeric-only fields. Filters
 * every keystroke/paste through numericInput.sanitizeNumericString so invalid
 * characters never make it into form state, instead of relying on submit-time
 * validation alone.
 */
const NumericTextField: React.FC<NumericTextFieldProps> = ({
    id,
    label,
    value,
    onChange,
    onInvalid,
    mode,
    decimalPlaces,
    allowNegative,
    max,
    maxLength,
    tone,
    message,
    placeholder,
    disabled,
}) => {
    const displayValue = value === null || value === undefined ? "" : String(value);
    const filterOpts: NumericFilterOptions = { mode, decimalPlaces, allowNegative, max, maxLength };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value;
        const sanitized = sanitizeNumericString(raw, filterOpts);
        if (raw !== "" && sanitized !== raw && onInvalid) {
            onInvalid();
        }
        onChange(sanitized);
    };

    return (
        <TextField
            id={id}
            label={label}
            value={displayValue}
            tone={tone}
            message={message}
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleChange}
        />
    );
};

export default NumericTextField;
